import { PartialVisitor } from 'sb-lib/types';
import { UniqueVisitor, ActiveVisitor, Client, SelfUser } from 'types';
import log from './log';

type AnyVisitorType = Client | ActiveVisitor | UniqueVisitor | PartialVisitor;

export const getVisitorDisplay = (visitor?: AnyVisitorType | null) => {
  return {
    displayName: getVisitorDisplayName(visitor),
    email: getVisitorEmail(visitor),
  };
};

export const getVisitorDisplayName = (
  visitor?: AnyVisitorType | null,
): string | undefined => {
  if (visitor) {
    if ('contact' in visitor && visitor.contact?.name) {
      return visitor.contact.name;
    }
    if ('displayName' in visitor && visitor.displayName) {
      return visitor.displayName;
    }
    if (
      visitor.customMetadata?.displayName &&
      typeof visitor.customMetadata?.displayName === 'string'
    ) {
      return visitor.customMetadata.displayName;
    }
    return 'Unknown';
  }
};

export const getVisitorCompanyName = (
  visitor?: AnyVisitorType | null,
): string | undefined => {
  if (visitor) {
    if ('contact' in visitor && visitor.contact?.customMetadata?.orgName) {
      return visitor.contact?.customMetadata?.orgName as string;
    }
    if ('contact' in visitor && visitor.contact?.company) {
      return visitor.contact?.company.name as string;
    }
  }
  return '';
};

export const getVisitorEmail = (visitor?: AnyVisitorType | null) => {
  const email = visitor?.email;
  if (email && typeof email === 'string') {
    return email;
  }
  if (visitor && `contact` in visitor && typeof visitor.contact?.email === 'string') {
    return visitor.contact.email;
  }
};

export const getVisitorDomain = (visitor?: AnyVisitorType | null) => {
  const email = getVisitorEmail(visitor);
  if (email) {
    return email.split('@')[1];
  }
};

/**
 * Mutatively convert an object with nested keys to dot-notation
 * flat object with only renderable keys.
 */
export const flattenVisitorCustomMetadata = (
  metadata: Record<any, any> | null | undefined,
) => {
  if (!metadata) {
    return metadata;
  }
  return recursivelyFlattenMetadata({}, metadata);
};

const recursivelyFlattenMetadata = (
  topObject: Record<any, any>,
  nestedObject: Record<any, any>,
  keyPrefix = '',
): Record<string, string | number | boolean | undefined> => {
  Object.entries(nestedObject).forEach(([key, value]) => {
    try {
      const wholeKey = `${keyPrefix ? `${keyPrefix}.` : ''}${key}`;
      // Basic data types go as-is
      if (
        value === null ||
        value === undefined ||
        ['boolean', 'string', 'number'].includes(typeof value)
      ) {
        topObject[wholeKey] = value;
      }
      // Convert arrays to comma-separated strings
      else if (Array.isArray(value)) {
        topObject[wholeKey] = value.join(', ');
      }
      // Everything else either gets recursively iterated over, or stringified if custom toString
      else {
        if (value.toString === Object.prototype.toString) {
          recursivelyFlattenMetadata(topObject, value, wholeKey);
        } else {
          topObject[wholeKey] = value.toString();
        }
      }
    } catch (err) {
      log.error(
        'Encountered an error while flattening custom metadata',
        { key, value },
        err,
      );
    }
  });
  return topObject;
};

export const isRoutedToMe = (user: SelfUser, visitor: Client) => {
  return visitor.routedUserIds.length === 0 || visitor.routedUserIds.includes(user.id);
};
